import React from 'react'
import { useHighlightComment } from './HighlightCommentProvider'
import { CommentBadge, HighlightBadge } from './icons'
import { useTranslation } from '../../hooks/useTranslation'

const CommentBox = () => {
  const {
    textAreaRef,
    comment,
    setComment,
    hasComment,
    editingComment,
    locator,
  } = useHighlightComment()
  let containerClass =
    'mt-2 mb-8 rounded-2xl bg-intoo-haze p-2 ring-2 font-normal'
  editingComment
    ? (containerClass += ' ring-primary')
    : (containerClass += ' ring-alto-300')
  const { t } = useTranslation('CommentBox')

  return (
    <div className="">
      <CommentHighlightBadges />
      {hasComment && (
        <div className={containerClass}>
          <textarea
            ref={textAreaRef}
            className="h-24 w-full resize-none rounded-md border-none bg-intoo-haze p-2 text-[#565656] focus:ring-transparent"
            placeholder={t('type_your_comment_here')}
            onChange={(e) => setComment(e.target.value)}
            value={comment}
            disabled={!editingComment}
            aria-label={t('notes_for_article', { locator })}
          ></textarea>
          <hr className="border-alto-300" />
          <div className="mt-4 flex justify-end">
            {editingComment ? (
              <EditingCommentButtons />
            ) : (
              <ViewingCommentButtons />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
export default CommentBox

const EditingCommentButtons = () => {
  const { saveComment, cancelComment } = useHighlightComment()
  const { t } = useTranslation('CommentBox')
  return (
    <>
      <button
        className="mr-2 rounded-3xl px-6 py-2 hover:bg-[#F3F3F3]"
        onClick={cancelComment}
      >
        {t('cancel')}
      </button>
      <button
        onClick={saveComment}
        className="rounded-3xl bg-primary px-6 py-2 text-white hover:bg-primary-darker-75"
      >
        {t('save')}
      </button>
    </>
  )
}

const ViewingCommentButtons = () => {
  const { editComment, deleteComment } = useHighlightComment()
  const { t } = useTranslation('CommentBox')
  return (
    <>
      <button
        className="mr-2 rounded-3xl px-6 py-2 hover:bg-[#F3F3F3]"
        onClick={deleteComment}
      >
        {t('delete')}
      </button>
      <button
        onClick={editComment}
        className="rounded-3xl bg-primary px-6 py-2 text-white hover:bg-primary-darker-75"
      >
        {t('edit')}
      </button>
    </>
  )
}

const CommentHighlightBadges = () => {
  const {
    hasHighlight,
    hasComment,
    editingComment,
    setEditingComment,
    removeHighlight,
  } = useHighlightComment()

  const toggleEditingComment = () => {
    setEditingComment(!editingComment)
  }

  const handleCommentKeyDown = (e) => {
    if ([13, 12].includes(e.keyCode)) {
      toggleEditingComment()
    }
  }

  const handleHighlightKeyDown = (e) => {
    if ([13, 12].includes(e.keyCode)) {
      removeHighlight()
    }
  }

  const { t } = useTranslation('CommentBox')

  return (
    <div className="items-left mt-2 flex">
      {hasComment && (
        <button
          onKeyDown={handleCommentKeyDown}
          onClick={toggleEditingComment}
          title={t('toggle_editing_comment')}
        >
          <CommentBadge
            title={t('this_text_has_a_comment')}
            className="mr-4"
            active={editingComment}
          />
        </button>
      )}
      {hasHighlight && (
        <button
          onKeyDown={handleHighlightKeyDown}
          onClick={removeHighlight}
          title={t('remove_highlight')}
        >
          <HighlightBadge active title={t('this_text_is_highlighted')} />
        </button>
      )}
    </div>
  )
}
